import type { NavigateFunction, Params as RouteParams } from 'react-router-dom'

import { BulkActionDefinition } from '@factorialco/f0-react/dist/experimental'
import {
  Delete,
  Download,
  Envelope,
  MoneyBag,
  Plus,
  ThumbsDown,
  ThumbsUp,
} from '@factorialco/f0-react/icons/app'
import { Features } from 'generated/featureDefinitions'
import type { AttendanceCustomPeriodEmployeeItemFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import { STATUS_FILTER_VALUES } from 'modules/attendance/components/TimeTrackingCollection/lib/constants'
import { Cycle } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { useTimeTrackingData } from 'modules/attendance/lib/timeTrackingDataContext'

interface SelectedEmployeeItem {
  item: AttendanceCustomPeriodEmployeeItemFragment
  checked: boolean
}

interface BulkActionParams {
  itemsStatus: readonly SelectedEmployeeItem[]
  periodType?: Cycle
  selectAll?: boolean
  totalCount?: number
}

interface BulkActionsReturn {
  primary: BulkActionDefinition[]
  secondary?: BulkActionDefinition[]
}

interface ReviewStatusResult {
  __typename: string
  count?: number[] | null
  values: string[]
}

interface Params {
  isDateNavigationToday: boolean
  params?: Readonly<RouteParams<string>>
  navigate?: NavigateFunction
  navigation?: Navigation<NavigationPaths>
  reviewStatusResult?: ReviewStatusResult | null
}

export const useBulkActionsConfig = ({
  isDateNavigationToday,
  params,
  navigate,
  navigation,
  reviewStatusResult,
}: Params) => {
  const { filters } = useTimeTrackingData()
  const { featureAvailability: hasPayroll } = useAttendanceFeatures(Features.payroll_management)
  const { featureAvailability: hasCompensations } = useAttendanceFeatures(
    Features.compensations_management
  )
  const getAllHaveStatus = (
    selectedEmployeeItems: AttendanceCustomPeriodEmployeeItemFragment[],
    totalItemsCount: number,
    targetStatus: 'pending' | 'approved',
    selectAll?: boolean,
    itemsStatus?: readonly SelectedEmployeeItem[]
  ): boolean => {
    // Case 0 (most basic): if selectAll is false, check if all selected items individually have target status
    if (!selectAll && selectedEmployeeItems.length > 0) {
      return selectedEmployeeItems.every((employee) => employee.reviewStatus === targetStatus)
    }

    // First case (simplest): if all visible items are selected, evaluate based on selected items
    if (selectedEmployeeItems.length === totalItemsCount && selectedEmployeeItems.length > 0) {
      return selectedEmployeeItems.every((employee) => employee.reviewStatus === targetStatus)
    }

    // Second case: if target status filter is active, we know all visible items have that status
    const reviewStatusFilter = Array.isArray(filters?.reviewStatus) ? filters.reviewStatus : []
    const hasTargetStatusFilter =
      reviewStatusFilter.length === 1 && reviewStatusFilter[0] === targetStatus

    if (hasTargetStatusFilter) {
      return selectedEmployeeItems.length > 0
    }

    // Third case (most complex): if selectAll is true, check if target status count equals (total - unchecked)
    if (selectAll && reviewStatusResult && itemsStatus) {
      const targetStatusIndex = reviewStatusResult.values?.findIndex(
        (status) => status === targetStatus
      )
      const targetStatusCount =
        targetStatusIndex !== undefined && targetStatusIndex >= 0
          ? (reviewStatusResult.count?.[targetStatusIndex] ?? 0)
          : 0

      const uncheckedItems = itemsStatus.filter((item) => !item.checked)
      const uncheckedCount = uncheckedItems.length
      const totalSelectedCount = totalItemsCount - uncheckedCount

      // Count how many unchecked items have the target status
      const uncheckedWithTargetStatus = uncheckedItems.filter(
        (item) => item.item.reviewStatus === targetStatus
      ).length

      // Adjust targetStatusCount: subtract unchecked items that have target status
      // because they're not selected but are counted in the analytics
      const adjustedTargetStatusCount = targetStatusCount - uncheckedWithTargetStatus

      return adjustedTargetStatusCount === totalSelectedCount && totalSelectedCount > 0
    }

    return false
  }

  const getAllHavePendingStatus = (
    selectedEmployeeItems: AttendanceCustomPeriodEmployeeItemFragment[],
    totalItemsCount: number,
    selectAll?: boolean,
    itemsStatus?: readonly SelectedEmployeeItem[]
  ): boolean => {
    return getAllHaveStatus(
      selectedEmployeeItems,
      totalItemsCount,
      'pending',
      selectAll,
      itemsStatus
    )
  }

  const getAllHaveApprovedStatus = (
    selectedEmployeeItems: AttendanceCustomPeriodEmployeeItemFragment[],
    totalItemsCount: number,
    selectAll?: boolean,
    itemsStatus?: readonly SelectedEmployeeItem[]
  ): boolean => {
    return getAllHaveStatus(
      selectedEmployeeItems,
      totalItemsCount,
      'approved',
      selectAll,
      itemsStatus
    )
  }

  const getBulkActions = (bulkActionParams: BulkActionParams): BulkActionsReturn => {
    const selectedEmployeeItems =
      bulkActionParams?.itemsStatus
        ?.filter((item) => item.checked && item.item.id != null)
        ?.map((item) => item.item) || []

    const totalItemsCount =
      bulkActionParams?.totalCount ?? bulkActionParams?.itemsStatus?.length ?? 0

    // Only evaluate pending status first, if true we don't need to check approved
    const allHavePendingStatus = getAllHavePendingStatus(
      selectedEmployeeItems,
      totalItemsCount,
      bulkActionParams.selectAll,
      bulkActionParams.itemsStatus
    )

    // Only evaluate approved status if pending is false
    const allHaveApprovedStatus = allHavePendingStatus
      ? false
      : getAllHaveApprovedStatus(
          selectedEmployeeItems,
          totalItemsCount,
          bulkActionParams.selectAll,
          bulkActionParams.itemsStatus
        )

    const primaryActions: BulkActionDefinition[] = []
    const secondaryActions: BulkActionDefinition[] = []

    //  Download Report as secondary action (in the dropdown), we can consider showing it only when bulkActionParams.selectAll
    if (params && navigate && navigation) {
      secondaryActions.push({
        label: i18n.t('time_tracking.menu.download_title'),
        description: i18n.t('time_tracking.menu.download_description'),
        icon: Download,
        id: 'download-report',
        keepSelection: true, // Keep selection after clicking download
      })
    }

    if (allHavePendingStatus) {
      primaryActions.push({
        label: i18n.t('time_tracking.table_collection.bulk_actions.approve'),
        icon: ThumbsUp,
        id: 'approve',
      })
    }

    if (allHaveApprovedStatus) {
      const canCompensatePeriodType =
        bulkActionParams.periodType === Cycle.monthly ||
        bulkActionParams.periodType === Cycle.weekly
      const showCompensationButton = canCompensatePeriodType && (hasPayroll || hasCompensations)

      if (showCompensationButton) {
        primaryActions.push({
          label: i18n.t('time_tracking.table_collection.bulk_actions.compensate'),
          icon: MoneyBag,
          id: 'compensate',
          keepSelection: true,
        })
      }

      primaryActions.push({
        label: i18n.t('time_tracking.table_collection.bulk_actions.disapprove'),
        icon: ThumbsDown,
        id: 'disapprove',
      })
    }

    if (isDateNavigationToday) {
      primaryActions.push({
        label: i18n.t('time_tracking.table_collection.bulk_actions.send_reminder'),
        icon: Envelope,
        id: 'send-reminder',
      })
    }

    if (selectedEmployeeItems.length > 0) {
      // Use status filter to determine exclude/include actions
      // Non-attendable employees only appear when ONLY_NON_ATTENDABLE filter is active
      const statusFilter = Array.isArray(filters?.status) ? filters.status : []
      const onlyNonAttendableFilter = statusFilter.includes(
        STATUS_FILTER_VALUES.ONLY_NON_ATTENDABLE
      )

      const hasAttendableEmployees = !onlyNonAttendableFilter
      const hasNonAttendableEmployees = onlyNonAttendableFilter === true

      if (hasAttendableEmployees) {
        primaryActions.push({
          label: i18n.t('time_tracking.table_collection.bulk_actions.exclude_from_timesheet'),
          icon: Delete,
          id: 'exclude',
        })
      }

      if (hasNonAttendableEmployees) {
        primaryActions.push({
          label: i18n.t('time_tracking.table_collection.bulk_actions.include_in_timesheet'),
          icon: Plus,
          id: 'include',
        })
      }
    }

    return {
      primary: primaryActions || [],
      secondary: secondaryActions,
    }
  }

  return { getBulkActions }
}
